import React from "react";

import { IconCalendar, IconPerson, Label, IconChevron } from "@busbud/horizon";
import type { InternationalTime } from "@busbud/int-schemas/udm";

import { OriginDestinationLabel } from "@app/components/origin-destination-label/origin-destination-label";
import { useAppContext } from "@app/helpers/hooks";
import { getPurchaseTotalPassengersCount } from "@app/helpers/purchase/get-purchase-passengers-count";
import { getPurchaseTrip } from "@app/helpers/purchase/get-purchase-trip";
import {
  getOutboundDepartureInternationalTime,
  getReturnDepartureInternationalTime
} from "@app/helpers/udm/get-departure-time";
import { getOutboundLegStops } from "@app/helpers/udm/get-leg-stops";
import { isRoundtrip } from "@app/helpers/udm/is-roundtrip";
import type IntlProviderService from "@app/services/intl-provider";
import type { TranslatorService } from "@app/services/translator";
import type { NapiV2Purchase } from "@app/types/napi-v2-purchase-types";

import { TripItemStatus } from "./trip-item-status";
import { TripItemImage } from "../../../../../../components/trip-item-image";

interface Props {
  archived?: boolean;
  purchase: NapiV2Purchase;
}

const getPassengersCountTranslation = (
  passengers_count: number,
  translator: TranslatorService
) => {
  const data = { n: passengers_count };

  return passengers_count > 1
    ? translator.t("!accounts.my-tickets.passengers", data)
    : translator.t("!accounts.my-tickets.passenger", data);
};

const formatDate = (
  intlProvider: IntlProviderService,
  date: InternationalTime
): string | null =>
  intlProvider.formatDate(date.timestamp, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });

export const TripItem: React.FC<Props> = ({ archived = false, purchase }) => {
  const { intlProvider, translator } = useAppContext();

  const trip = getPurchaseTrip(purchase);
  const outbound_stops = getOutboundLegStops(trip);
  const is_roundtrip = isRoundtrip(trip);
  const outbound_departure_time = getOutboundDepartureInternationalTime(trip);
  const return_departure_time = is_roundtrip
    ? getReturnDepartureInternationalTime(trip)
    : null;

  const passengers_count = getPurchaseTotalPassengersCount(purchase);

  return (
    <div className="flex items-center gap-200">
      <TripItemImage
        image_url={outbound_stops.origin?.city?.image_url || null}
        archived={archived}
      />
      <div className="flex grow-0 flex-col-reverse items-start justify-end sm:grow sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col sm:gap-050">
          <div className="max-w-[90%] self-start sm:hidden">
            <OriginDestinationLabel
              as="span"
              label_size="md"
              origin={outbound_stops.origin?.city?.name || ""}
              destination={outbound_stops.destination?.city?.name || ""}
              variant={is_roundtrip ? "roundtrip" : "one-way"}
            />
          </div>
          <div className="hidden self-start sm:block">
            <OriginDestinationLabel
              as="span"
              label_size="lg"
              origin={outbound_stops.origin?.city?.name || ""}
              destination={outbound_stops.destination?.city?.name || ""}
              variant={is_roundtrip ? "roundtrip" : "one-way"}
            />
          </div>
          <div className="flex flex-wrap gap-100">
            <div className="flex items-center gap-050">
              <IconCalendar size="sm" className="text-icon-color-primary" />
              <Label
                as="span"
                className="text-color-secondary"
                size="sm"
                fontWeight="bold"
                noWrap
              >
                {formatDate(intlProvider, outbound_departure_time)}{" "}
                {!!return_departure_time &&
                  ` - ${formatDate(intlProvider, return_departure_time)}`}
              </Label>
            </div>
            <div className="flex items-center gap-050">
              <IconPerson
                variant={passengers_count > 1 ? "multiple" : "default"}
                className="text-icon-color-primary"
                size="sm"
              />
              <Label
                as="span"
                className="text-color-secondary"
                size="sm"
                fontWeight="bold"
              >
                <span className="hidden md:block">
                  {getPassengersCountTranslation(passengers_count, translator)}
                </span>
                <span className="block md:hidden">{passengers_count}</span>
              </Label>
            </div>
          </div>
        </div>
        <div className="mb-100 flex items-center gap-150 sm:mb-0">
          <TripItemStatus
            archived={archived}
            trip={trip}
            cancellation={purchase.cancellation ?? null}
          />
          <IconChevron
            variant="right"
            size="md"
            className="hidden text-icon-color-primary sm:block"
          />
        </div>
      </div>
    </div>
  );
};
